// Pure array operations for design elements.
import type { DesignBadgeItem, DesignElement, DesignTableCell } from '@/types/blocks'
import { DESIGN_LIMITS, newDesignId } from './designElementDefaults'

export {
  DESIGN_LIMITS,
  createDesignElement,
  newBadgeItem,
  newTableCell,
} from './designElementDefaults'

/** Типы юниона, у которых студия НЕ рисует ни превью в канвасе, ни формы в панели.
 *  Сейчас таких НЕТ — отсюда `never`, и это не заглушка, а сам гейт: обе ветки-фолбэка
 *  (`DesignElementView`, `DesignElementSettings`) заканчиваются присваиванием сужённого
 *  элемента в этот тип. Добавили тип в юнион и забыли ветку — сужение перестанет быть
 *  пустым, перестанет быть присваиваемым, и ОБА реестра упадут компиляцией, а не молча
 *  покажут заглушку. Появится тип, который мы намеренно не рисуем, — впишите его сюда
 *  вместо `never` (так тут жила `animation`, пока её не сняли 2026-07-29).
 *  В рантайме в те же ветки приходят легаси-типы бека (`button`/`divider`/`animation`),
 *  которых в юнионе нет вовсе — поэтому `type` наружу отдаётся строкой, а не литералом. */
export type DesignElementWithoutUi = never

/** Коллекция элемента может прийти отсутствующей или null — тип этого не отражает,
 *  но данные бека такое допускают, а `element.badges.map` уносил TypeError'ом ВЕСЬ
 *  канвас и правую панель. Битый элемент обязан деградировать в пустой, а не в краш. */
export const badgeItems = (badges: DesignBadgeItem[] | null | undefined): DesignBadgeItem[] =>
  Array.isArray(badges) ? badges : []

/** То же для матрицы таблицы (см. `badgeItems`). */
export const tableRows = (data: DesignTableCell[][] | null | undefined): DesignTableCell[][] =>
  Array.isArray(data) ? data : []

/** Ширина таблицы — по самой ШИРОКОЙ строке, а не по шапке: рваные данные (импорт,
 *  старый бек) иначе читались бы формой и канвасом по-разному, и форма разрешила бы
 *  столбец сверх лимита — то самое 422. Пустая матрица отдаёт минимум, чтобы
 *  «добавить строку» не создало строку из нуля ячеек. */
export const tableColumnCount = (data: DesignTableCell[][]): number =>
  data.length === 0 ? DESIGN_LIMITS.TABLE_COLS_MIN : Math.max(1, ...data.map((row) => row.length))

export const updateElementAt = (
  elements: DesignElement[],
  id: string,
  patch: Partial<DesignElement>,
): DesignElement[] =>
  elements.map((el) => (el.id === id ? ({ ...el, ...patch } as DesignElement) : el))

export const removeElement = (elements: DesignElement[], id: string): DesignElement[] =>
  elements.filter((el) => el.id !== id)

export const moveElement = (
  elements: DesignElement[],
  from: number,
  to: number,
): DesignElement[] => {
  const last = elements.length - 1
  if (from < 0 || to < 0 || from > last || to > last || from === to) return elements
  const next = [...elements]
  const [moved] = next.splice(from, 1)
  next.splice(to, 0, moved)
  return next
}

/** Копия элементов со свежими id — для дублирования блока (id не шэрятся).
 *
 *  ЧЕТВЁРТАЯ точка регистрации типа (после карточки палитры, рендера и формы) —
 *  и самая незаметная: тип с вложенными id, забытый здесь, ничего не ломает на
 *  экране, а просто отдаёт копии блока ЧУЖИЕ id бейджей/ячеек. Поэтому не цепочка
 *  `if` с общим «остальное как есть», а switch, перечисляющий ВСЕ типы юниона:
 *  забытый тип провалится в `default`, где гейт `never` не даст скомпилироваться. */
export const regenerateElementIds = (elements: DesignElement[]): DesignElement[] =>
  elements.map((el) => {
    const withId = { ...el, id: newDesignId() }
    switch (withId.type) {
      case 'badge':
        return {
          ...withId,
          badges: badgeItems(withId.badges).map((b) => ({ ...b, id: newDesignId() })),
        }
      case 'table':
        return {
          ...withId,
          tableData: tableRows(withId.tableData).map((row) =>
            row.map((c) => ({ ...c, id: newDesignId() })),
          ),
        }
      // Вложенных id нет — хватает свежего id самого элемента.
      case 'heading':
      case 'text':
      case 'image':
        return withId
      default: {
        // Легаси-типы бека (`button`/`divider`/`animation`) в юнион не входят: статически
        // сюда не попадает никто, в рантайме — попадают, и копировать их надо как есть.
        const _exhaustive: never = withId
        return withId
      }
    }
  })
